import { TestResults, FvcTestRepeatabilitySummary, PefTestRepeatabilitySummary, FvcTestAcceptabilitySummary } from "../../PulmonaryFunctionTests/Shared/PFTAPITypes";
import { ParameterTypeName } from "./ParameterTypes";
import { SelectedFixedCommentMeta } from "./FixedComments/apiTypes";
import { FvcAnalysisSet, FvcSession, PefAnalysisSet, PefSession } from "webportalclient";

export function MapFvcTestAcceptabilitySummaries(testResults: TestResults[]): Array<FvcTestRepeatabilitySummary> {
    return testResults.map(tr => {
        return {
            TestId: tr.TestId,
            Accepted: tr.OverreaderAcceptability,
            Fvc: tr.TestParameters.find(tp => tp.ParameterType === ParameterTypeName.PARAM_FVC).Value,
            Fev1: tr.TestParameters.find(tp => tp.ParameterType === ParameterTypeName.PARAM_FEV1).Value,
            Pef: tr.TestParameters.find(tp => tp.ParameterType === ParameterTypeName.PARAM_PEFMIN).Value,
            Fef2575: tr.TestParameters.find(tp => tp.ParameterType === ParameterTypeName.PARAM_FEF2575).Value,
            Fev1Fvc: tr.TestParameters.find(tp => tp.ParameterType === ParameterTypeName.PARAM_FEV1FVC).Value,
            Fev6: tr.TestParameters.find(tp => tp.ParameterType === ParameterTypeName.PARAM_FEV6).Value,
            PefSec: tr.TestParameters.find(tp => tp.ParameterType === ParameterTypeName.PARAM_PEFSEC).Value,
        } as FvcTestRepeatabilitySummary;
    })
}

export function MapFvcTestRepeatabilitySummariesForAnalysisSet(analysisSet: FvcAnalysisSet, session: FvcSession): Array<FvcTestRepeatabilitySummary> {
    return analysisSet.tests.filter(ast => session.tests.map(t => t.id).includes(ast.testId)).map(ast => {
        return {
            TestId: ast.testId,
            Accepted: ast.accepted,
            Fvc: session.tests.find(t => t.id === ast.testId).fvc,
            Fev1: session.tests.find(t => t.id === ast.testId).fev1,
            Pef: session.tests.find(t => t.id === ast.testId).pefMin,
            Fef2575: session.tests.find(t => t.id === ast.testId).fef2575,
            Fev1Fvc: session.tests.find(t => t.id === ast.testId).fev1Fvc,
            Fev6: session.tests.find(t => t.id === ast.testId).fev6,
            PefSec: session.tests.find(t => t.id === ast.testId).pefSec
        } as FvcTestRepeatabilitySummary;
    })
}

export function MapFvcTestAcceptabilitySummariesForAnalysisSet(analysisSet: FvcAnalysisSet, session: FvcSession): Array<FvcTestAcceptabilitySummary> {
    return analysisSet.tests
        .filter(ast => session.tests.map(t => t.id).includes(ast.testId))
        .map(ast => {
            const test = session.tests.find(t => t.id === ast.testId);
            return {
                TestId: ast.testId,
                TestNumber: ast.test.index,
                Accepted: ast.accepted,
                Fev1Accepted: ast.fev1Acceptability,
                FvcAccepted: ast.fvcAcceptability,
                Fvc: test.fvc,
                Fev1: test.fev1,
                Fev6: test.fev6,
                Tv: test.tv,
                Fivc: test.fivc,
                PefMin: test.pefMin,
                PefSec: test.pefSec,
                PifMin: test.pifMin,
                PifSec: test.pifSec,
                Fev3: test.fev3,
                Fev05: test.fev05,
                Fev075: test.fev075,
                Fiv1: test.fiv1,
                Mvvind: test.mvvInd,
                Ivc: test.ivc,
                FefMax: test.fefMax,
                Fev1Vc: test.fev1Vc,
                Fev1Ht2: test.fev1Ht2,
                Fev3Vc: test.fev3Vc,
                Tlc: test.tlc,
                Rv: test.rv,
                Frc: test.frc,
                Fev1Q: test.fev1Q,
                Fef2575: test.fef2575,
                Fef0212: test.fef0212,
                Fef7585: test.fef7585,
                Fef25: test.fef25,
                Fef50: test.fef50,
                Fef75: test.fef75,
                Fmft: test.fmft,
                Fev1Fev6: test.fev1Fev6,
                Fev1Fvc: test.fev1Fvc,
                Fev1R: test.fev1R,
                Fev05Fvc: test.fev05Fvc,
                Fev075Fvc: test.fev075Fvc,
                Fev3Fvc: test.fev3Fvc,
                Fev6Fvc: test.fev6Fvc,
                Fef2575Fvc: test.fef2575Fvc,
                FivcFvc: test.fivcFvc,
                Fev1Pef: test.fev1Pef,
                Fif50Fef50: test.fif50Fef50,
                Fev1Fivc: test.fev1Fivc,
                Fiv1Fivc: test.fiv1Fivc,
                Fiv1Fvc: test.fiv1Fvc,
                Fef50Fif50: test.fef50Fif50,
                Fev1Ivc: test.fev1Ivc,
                Fev3Fev6: test.fev3Fev6,
                Ic: test.ic,
                Irv: test.irv,
                Erv: test.erv,
                Fet: test.fet,
                Fif25: test.fif25,
                Fif50: test.fif50,
                Fif75: test.fif75,
                Rawind: test.rawind
            } as FvcTestAcceptabilitySummary;
        });
}

export function MapPefTestAcceptabilitySummariesForAnalysisSet(analysisSet: PefAnalysisSet, siteAnalysisSet: PefAnalysisSet, session: PefSession): Array<PefTestRepeatabilitySummary> {
    const setTests = analysisSet.tests.length > 0 ? analysisSet.tests : siteAnalysisSet.tests;


    return setTests.filter(ast => session.tests.map(t => t.id).includes(ast.testId)).map(ast => {
        return {
            TestId: ast.testId,
            Accepted: ast.accepted,
            PefMin: session.tests.find(t => t.id === ast.testId).pefMin,
            PefSec: session.tests.find(t => t.id === ast.testId).pefSec,
        } as PefTestRepeatabilitySummary;
    })
}

export interface SpirometryFixedComment extends SelectedFixedCommentMeta {

}
